import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Toolbar from "@mui/material/Toolbar";
import MenuIcon from "@mui/icons-material/Menu";
import StoreIcon from "@mui/icons-material/Store";
import WorkIcon from "@mui/icons-material/Work";
import PeopleIcon from "@mui/icons-material/People";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useLocation, useNavigate } from "react-router-dom";
import { AccountCircle } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../../../context/AuthProvider/useAuth";
import { generalWhite, generalBlack } from "../../../styles/colors";
import { Divider, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import {
    CURRENT_SUBDOMAIN,
    DEMANDORIA_SUBDOMAINS,
} from "../../../utils/stringUtils";
import { handleLogout } from "../../../utils/miscelaneous";
import useManagerStores from "../../../hooks/context-hooks/useManagerStores";
import { setLocalStorageItem } from "../../../hooks/localStorageHooks";
import TranslatorButton from "@/components/general/TranslatorButton";
import { useTranslation } from "react-i18next";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

const DRAWER_WIDTH = 300;

interface ComercialDrawerProps {
  content: any;
  windowObject?: () => Window;
}

const drawerIcons = [
  <CalendarMonthIcon />,
  <PendingActionsIcon />,
  <StoreIcon />,
  <AccountBalanceWalletIcon />,
  <WorkIcon />,
  <PeopleIcon />,
  <LogoutIcon />,
];

const ComercialDrawer = (props: ComercialDrawerProps) => {
  const { t } = useTranslation();
  const { windowObject, content } = props;

  const [mobileOpen, setMobileOpen] = useState(false);

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  let managerStoresContext = useManagerStores();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleMyProfileClick = (): void => {
    setLocalStorageItem("userData", auth.user);
    navigate("/user/details");
  };

  const handleQuitClick = () => {
    if (confirm(t("components.comercialDrawer.logoutConfirm"))) {
      managerStoresContext = null;
      handleLogout(auth, navigate);
    }
  };

  const sidebarDivider = (
    <Box display="flex" flexDirection="column" margin="10px 5px" gap={0.5}>
      <Divider sx={{ width: "100%", padding: "0px 5px" }} />
    </Box>
  );

const checkIfIsASelectablePath = (path: string) =>
    location.pathname === path ||
    location.pathname === `${path}/add` ||
    location.pathname === `${path}/edit` ||
    location.pathname === `${path}/details`;

const drawerItems = [
    {
        id: "appointments",
        label: t("components.comercialDrawer.items.appointments"),
        path: "/appointments",
    },
    {
        id: "stores",
        label: t("components.comercialDrawer.items.stores"),
        path: "/stores",
    },
    {
        id: "finance",
        label: t("components.comercialDrawer.items.finance") || "Financeiro",
        path: "/finance",
    },
    {
        id: "services",
        label: t("components.comercialDrawer.items.services"),
        path: "/services",
    },
    {
        id: "employees",
        label: t("components.comercialDrawer.items.employees"),
        path: "/employees",
    },
    {
        id: "workScales",
        label: t("components.comercialDrawer.items.workScales"),
        path: "/my-workscales",
    },
    {
        id: "logout",
        label: t("components.comercialDrawer.items.logout"),
        path: "/login",
    },
];

const drawer = (
    <Box
      sx={{
        backgroundColor: "primary.main",
        height: "100%",
        color: generalWhite,
      }}
    >
      <Toolbar
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Typography variant="h5" fontWeight="bold">
          {CURRENT_SUBDOMAIN === DEMANDORIA_SUBDOMAINS[0]
            ? t("components.comercialDrawer.titleBeauty")
            : t("components.comercialDrawer.titlePet")}
        </Typography>
     <TranslatorButton />
     </Toolbar>
    <List sx={{ marginBottom: "5px" }}>
        {drawerItems.map(({ id, label, path }, index) => {

          return (
            <Box key={id}>
            {id === "workScales" && sidebarDivider}
            <ListItem
                disablePadding
                sx={{
                width: "300px",
                bottom: id === "logout" ? "0" : "auto",
                position: id === "logout" ? "fixed" : "static",
                marginBottom: "5px",
                ":hover": {
                    backgroundColor: "rgba(0,0,0,0.1)",
                  },
                  ":hover .list-item-icon": {
                    color: generalWhite,
                  },
                  ":hover .list-item-text": {
                    color: generalWhite,
            },
        }}
    >
        <ListItemButton
            onClick={() =>
                id === "logout" ? handleQuitClick() : navigate(path)
            }
            sx={{
                    backgroundColor: checkIfIsASelectablePath(path)
                      ? "secondary.main"
                      : "transparent",
                  }}
                >
                  <ListItemIcon
                    className="list-item-icon"
                    sx={{
                      color: checkIfIsASelectablePath(path)
                        ? "primary.main"
                        : generalWhite,
                    }}
                  >
                    {drawerIcons[index % drawerIcons.length]}
                  </ListItemIcon>
                  <Typography
                    fontWeight={
                      checkIfIsASelectablePath(path) ? "bold" : "regular"
                    }
                    className="list-item-text"
                    sx={{
                      color: checkIfIsASelectablePath(path)
                        ? "primary.main"
                        : generalWhite,
                    }}
                >
                    {label}
                </Typography>
            </ListItemButton>
        </ListItem>

            {id === "appointments" && sidebarDivider}
            </Box>
          );
        })}
      </List>
    </Box>
  );

  const container =
    windowObject !== undefined ? () => windowObject().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          mr: "12px",
          pl: "12px",
          backgroundColor: generalWhite,
          boxShadow: "none",
          border: "none",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { sm: "none" } }}
          >
            <MenuIcon sx={{ color: generalBlack }} />
          </IconButton>

          <Box sx={{ flex: 1 }} />

          <Box display="flex" gap={1}>
            <Tooltip title={t("components.comercialDrawer.tooltips.myData")}>
              <IconButton size="large" onClick={handleMyProfileClick}>
                <AccountCircle sx={{ color: generalBlack }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              minWidth: DRAWER_WIDTH,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginTop: "70px",
          padding: "0px 25px",
          minWidth: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
    >
        {content}
      </Box>
    </Box>
  );
};

export default ComercialDrawer;
